/**
 * Ranked post rows (desktop + mobile) with quick actions.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { PlatformBadge } from "@/components/Brand";
import { ChartLine, ArrowsClockwise, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { RowCheckbox } from "@/components/BulkActionBar";
import { PostCard } from "@/components/posts/PostCard";
import { medalFromRank, tierFromScore, totalEngagement } from "@/components/posts/utils";

export function PostRow({ post, product, rank, onChanged, selected, onToggleSelect }) {
  const [expanded, setExpanded] = useState(false);
  const score = post.score || 0;
  const scoreColor = score >= 60 ? "#00C853" : score >= 30 ? "#FFCC00" : "#FF2A00";
  const tier = tierFromScore(score);
  const medal = medalFromRank(rank);
  const eng = totalEngagement(post);
  const imgs = post.imagenes_base64 || [];
  const heroIdx = Math.min(post.mejor_imagen_idx || 0, Math.max(0, imgs.length - 1));
  const thumb = imgs[heroIdx] ? `data:image/jpeg;base64,${imgs[heroIdx]}` : null;

  return (
    <>
      <div
        className={`grid grid-cols-12 px-4 py-3 border-b border-zinc-100 text-sm items-center hover:bg-zinc-50 cursor-pointer relative ${selected ? "bg-[#EEF2FF]" : ""}`}
        style={{ borderLeft: `3px solid ${tier.color}` }}
        data-testid={`post-row-${post.id}`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="col-span-1 flex items-center gap-1.5">
          <RowCheckbox
            checked={!!selected}
            onChange={onToggleSelect}
            testid={`select-post-${post.id}`}
          />
          {thumb ? (
            <div className="w-10 h-10 border border-zinc-200 overflow-hidden bg-zinc-50 shrink-0 relative">
              <img src={thumb} alt={post.titulo} className="w-full h-full object-cover" />
              {imgs.length > 1 && (
                <span className="absolute bottom-0 right-0 bg-klein text-white text-[8px] font-mono px-0.5 leading-none py-0.5">
                  {imgs.length}
                </span>
              )}
            </div>
          ) : (
            <div className="w-10 h-10 border border-zinc-200 bg-zinc-50 flex items-center justify-center text-zinc-400">
              <MegaphoneIcon />
            </div>
          )}
          {medal && (
            <span
              className="text-base leading-none"
              title={`${medal.label} por score`}
              data-testid={`post-medal-${post.id}`}
            >
              {medal.emoji}
            </span>
          )}
        </div>

        <div className="col-span-4 min-w-0">
          <div className="font-medium truncate">{post.titulo}</div>
          <div className="text-[11px] text-muted2 truncate">
            {post.tono} · v{post.version}
            {eng > 0 && <span className="ml-2 tabular-nums">· {eng} interacciones</span>}
          </div>
        </div>

        <div className="col-span-2">
          <PlatformBadge platform={post.plataforma} size="sm" />
        </div>

        <div className="col-span-2 min-w-0 text-xs truncate text-muted2">
          {product?.nombre || "Producto eliminado"}
        </div>

        <div className="col-span-1 text-right">
          <div
            className="h-display text-xl font-black tabular-nums leading-none"
            style={{ color: scoreColor }}
          >
            {score}
          </div>
          <div
            className="text-[9px] uppercase tracking-[0.15em] font-medium mt-0.5 px-1 py-0.5 inline-block"
            style={{ color: tier.color, background: tier.bg }}
          >
            {tier.label}
          </div>
        </div>

        <div className="col-span-1 text-right tabular-nums">
          <div className="text-xs font-medium">{post.metricas?.vistas || 0}</div>
          <div className="text-[10px] text-muted2">vistas</div>
        </div>

        <div className="col-span-1 flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <QuickActions post={post} onChanged={onChanged} setExpanded={setExpanded} />
        </div>
      </div>

      {expanded && (
        <div
          className="border-b border-zinc-200 bg-zinc-50 p-6"
          data-testid={`post-detail-${post.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <PostCard post={post} product={product} rank={rank} onChanged={onChanged} compact />
        </div>
      )}
    </>
  );
}


export function PostRowMobile({ post, product, rank, onChanged, selected, onToggleSelect }) {
  const [expanded, setExpanded] = useState(false);
  const score = post.score || 0;
  const scoreColor = score >= 60 ? "#00C853" : score >= 30 ? "#FFCC00" : "#FF2A00";
  const tier = tierFromScore(score);
  const medal = medalFromRank(rank);
  const eng = totalEngagement(post);
  const imgs = post.imagenes_base64 || [];
  const heroIdx = Math.min(post.mejor_imagen_idx || 0, Math.max(0, imgs.length - 1));
  const thumb = imgs[heroIdx] ? `data:image/jpeg;base64,${imgs[heroIdx]}` : null;

  return (
    <>
      <div
        className={`border border-zinc-200 p-3 flex gap-3 w-full text-left ${selected ? "bg-[#EEF2FF]" : ""}`}
        style={{ borderLeft: `3px solid ${tier.color}` }}
        data-testid={`post-card-mobile-${post.id}`}
      >
        <div className="shrink-0 pt-1">
          <RowCheckbox
            checked={!!selected}
            onChange={onToggleSelect}
            testid={`m-select-post-${post.id}`}
          />
        </div>
        <button
          type="button"
          className="flex gap-3 flex-1 min-w-0 text-left"
          onClick={() => setExpanded(!expanded)}
        >
        {thumb ? (
          <div className="w-14 h-14 border border-zinc-200 overflow-hidden bg-zinc-50 shrink-0 relative">
            <img src={thumb} alt={post.titulo} className="w-full h-full object-cover" />
            {medal && (
              <span className="absolute -top-1 -left-1 text-lg leading-none drop-shadow">{medal.emoji}</span>
            )}
          </div>
        ) : (
          <div className="w-14 h-14 border border-zinc-200 bg-zinc-50 shrink-0 flex items-center justify-center text-zinc-400">
            <MegaphoneIcon />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-medium text-sm truncate">{post.titulo}</div>
              <div className="text-[11px] text-muted2 truncate">
                {post.plataforma} · {product?.nombre || "—"}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div
                className="h-display text-xl font-black tabular-nums leading-none"
                style={{ color: scoreColor }}
              >
                {score}
              </div>
              <div
                className="text-[9px] uppercase tracking-[0.15em] font-medium mt-0.5 px-1 py-0.5 inline-block"
                style={{ color: tier.color, background: tier.bg }}
              >
                {tier.label}
              </div>
            </div>
          </div>
          <div className="text-[11px] text-muted2 mt-1.5 tabular-nums">
            {post.metricas?.vistas || 0} vistas · {eng} interacciones · v{post.version}
          </div>
        </div>
      </button>
      </div>

      {expanded && (
        <div
          className="bg-zinc-50 border border-zinc-200 border-t-0 p-4"
          data-testid={`post-detail-mobile-${post.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <PostCard post={post} product={product} rank={rank} onChanged={onChanged} compact />
        </div>
      )}
    </>
  );
}


function MegaphoneIcon() {
  return <span className="text-base">📢</span>;
}


function QuickActions({ post, onChanged, setExpanded }) {
  const [busy, setBusy] = useState(false);
  const improve = async () => {
    setBusy(true);
    try {
      await api.post(`/posts/${post.id}/improve`);
      toast.success("Publicación mejorada por IA");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al mejorar");
    } finally {
      setBusy(false);
    }
  };
  const onDelete = async () => {
    if (!window.confirm("Eliminar publicación?")) return;
    await api.delete(`/posts/${post.id}`);
    onChanged?.();
  };
  return (
    <>
      <button
        type="button"
        className="p-1.5 hover:bg-zinc-100 text-klein"
        onClick={() => setExpanded(true)}
        data-testid={`row-metrics-${post.id}`}
        title="Ver y registrar métricas"
      >
        <ChartLine size={14} />
      </button>
      <button
        type="button"
        className="p-1.5 hover:bg-zinc-100 text-klein disabled:opacity-40"
        disabled={busy}
        onClick={improve}
        data-testid={`row-improve-${post.id}`}
        title="Mejorar con IA"
      >
        <ArrowsClockwise size={14} />
      </button>
      <button
        type="button"
        className="p-1.5 hover:bg-zinc-100 text-[#FF2A00]"
        onClick={onDelete}
        data-testid={`row-delete-${post.id}`}
        title="Eliminar"
      >
        <Trash size={14} />
      </button>
    </>
  );
}


export function KPIBox({ label, value, icon: Icon, suffix }) {
  return (
    <div className="border border-zinc-200 p-4">
      <div className="overline flex items-center gap-1.5 text-klein">
        {Icon ? <Icon size={12} weight="fill" /> : null}
        {label}
      </div>
      <div className="h-display text-3xl font-black tabular-nums mt-2">
        {value}
        {suffix}
      </div>
    </div>
  );
}

// Helpers shared by Row and MobileRow
